/**
 * ConfidencePanel · Sprint 9.2.
 *
 * Presentation-only. Renders an operator-readable confidence report
 * for a supplier invoice. Reads the `confidence` block stored on the
 * invoice document. LAW-007: no business logic runs here.
 */
import { CheckCircle, Warning, Info, Sparkle, Shield } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useState } from "react";
import { api } from "@/lib/api";

const HIGH = 0.90;

export default function ConfidencePanel({ invoiceId, confidence, onRefreshed }) {
  const [busy, setBusy] = useState(false);

  if (!confidence) {
    return (
      <section
        data-testid="confidence-panel-missing"
        className="rounded-lg border border-slate-200 bg-slate-50 p-5"
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              AI Confidence
            </div>
            <div className="text-sm text-slate-700 mt-1">
              This invoice has not been scored yet — enrichment runs automatically
              for new uploads. You can re-run it now.
            </div>
          </div>
          <button
            type="button"
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/receiving/${invoiceId}/enrich-confidence`);
                toast.success("Confidence re-scored.");
                onRefreshed?.();
              } catch {
                toast.error("Could not re-score. Try again shortly.");
              } finally { setBusy(false); }
            }}
            data-testid="confidence-rescore-btn"
            disabled={busy}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
          >
            {busy ? "Scoring…" : "Score confidence"}
          </button>
        </div>
      </section>
    );
  }

  const pct = Math.round((confidence.overall_confidence || 0) * 100);
  const high = !!confidence.high_confidence;
  const label = high ? "High Confidence" : "Manual Review Required";
  const ringCls = high
    ? "border-emerald-300 bg-emerald-50"
    : pct >= 60
      ? "border-amber-300 bg-amber-50"
      : "border-rose-300 bg-rose-50";
  const pillCls = high
    ? "bg-emerald-600 text-white"
    : pct >= 60
      ? "bg-amber-600 text-white"
      : "bg-rose-600 text-white";

  return (
    <section
      data-testid="confidence-panel"
      className={`rounded-lg border ${ringCls} p-5 space-y-5`}
    >
      {/* Overall */}
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1">
            <Sparkle size={12} weight="fill" /> AI Confidence
          </div>
          <div className="mt-1 flex items-baseline gap-3">
            <div className="text-4xl font-semibold tabular-nums text-slate-900"
                 data-testid="confidence-overall-pct">{pct}%</div>
            <span
              data-testid="confidence-overall-label"
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${pillCls}`}
            >
              {high
                ? <CheckCircle size={14} weight="fill" />
                : <Warning size={14} weight="fill" />}
              {label}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Scored by {confidence.model || "—"} · {confidence.scored_at
              ? new Date(confidence.scored_at).toLocaleString()
              : "—"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider">Threshold</div>
          <div className="text-sm font-semibold text-slate-800 tabular-nums">
            {Math.round(HIGH * 100)}%
          </div>
        </div>
      </header>

      {/* Explanations */}
      {confidence.explanations?.length > 0 && (
        <div data-testid="confidence-explanations">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
            Confidence reduced because
          </div>
          <ul className="space-y-1">
            {confidence.explanations.map((e, i) => (
              <li key={i} className="text-sm text-slate-800 flex items-start gap-2">
                <Info size={14} className="text-amber-600 mt-0.5 shrink-0" />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {confidence.duplicate_of && (
        <div className="rounded-md border border-rose-200 bg-white px-3 py-2 text-sm text-rose-800"
             data-testid="confidence-duplicate-warning">
          Duplicate invoice detected — see existing document {String(confidence.duplicate_of).slice(0,8)}…
        </div>
      )}

      {/* Field-level */}
      <div data-testid="confidence-fields">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
          Field-level confidence
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {(confidence.fields || []).map((f) => (
            <FieldRow key={f.key} field={f} />
          ))}
        </div>
      </div>

      {confidence.warnings?.length > 0 && (
        <ul className="text-[11px] text-slate-500 space-y-1">
          {confidence.warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-2">
              <Shield size={12} className="mt-0.5" /> {w}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FieldRow({ field }) {
  const ai = field.ai_confidence;
  const aiPct = ai != null ? Math.round(ai * 100) : null;
  const ok = field.passed;
  return (
    <div
      className="rounded-md border border-slate-200 bg-white p-3"
      data-testid={`confidence-field-${field.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">{field.label}</div>
          <div className="text-sm font-mono text-slate-900 truncate">
            {formatValue(field.value)}
          </div>
        </div>
        <div className="text-right shrink-0">
          {aiPct != null ? (
            <div className="text-xs font-semibold tabular-nums text-slate-700">
              AI {aiPct}%
            </div>
          ) : (
            <div className="text-[10px] text-slate-400">no AI score</div>
          )}
          <div className="mt-1 flex items-center gap-1 justify-end text-[11px]">
            {ok
              ? <><CheckCircle size={12} weight="fill" className="text-emerald-600" /><span className="text-emerald-700">Validated</span></>
              : <><Warning size={12} weight="fill" className="text-amber-600" /><span className="text-amber-700">Check needed</span></>}
          </div>
        </div>
      </div>
      {/* Show any failing sub-check details */}
      {field.validations?.some((v) => !v.passed) && (
        <ul className="mt-1 space-y-0.5 text-[11px] text-amber-700">
          {field.validations.filter((v) => !v.passed).map((v, i) => (
            <li key={i}>• {v.label}{v.detail ? ` — ${v.detail}` : ""}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatValue(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return `${v.length} line${v.length === 1 ? "" : "s"}`;
  if (typeof v === "object") return JSON.stringify(v).slice(0, 40) + "…";
  return String(v);
}
